/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import Component from '@glimmer/component';
import {
  HdsDropdown,
  HdsFormTextInputField,
} from '@hashicorp/design-system-components/components';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from '../../utils/helpers.ts';

import './namespace-picker.scss';

export interface SSUNamespacePickerSignature {
  Args: {
    namespaces: string[];
    onNamespaceChange: (namespace: string) => void;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class SSUNamespacePicker extends Component<SSUNamespacePickerSignature> {
  @tracked selectedNamespace: string = this.args.namespaces[0] || '';
  @tracked search = '';

  get filteredNamespaces() {
    if (!this.search) return this.args.namespaces;
    return this.args.namespaces.filter((ns) =>
      ns.toLowerCase().includes(this.search.toLowerCase()),
    );
  }

  handleNamespaceSelection = (
    namespace: string,
    close: () => void = () => {},
  ) => {
    this.selectedNamespace = namespace;
    this.args.onNamespaceChange(namespace);

    close();
  };

  handleSearchInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.search = input.value;
  };

  <template>
    <HdsDropdown
      data-test-vault-reporting-namespace-picker
      class="ssu-namespace-picker"
      @height="300px"
      @listPosition="bottom-left"
      as |D|
    >
      <D.ToggleButton @color="secondary" @text={{this.selectedNamespace}} />
      <D.Header class="ssu-namespace-picker__header" @hasDivider={{true}}>
        <HdsFormTextInputField
          data-test-vault-reporting-namespace-search
          @type="search"
          placeholder="Search"
          @value={{this.search}}
          {{on "input" this.handleSearchInput}}
        />
      </D.Header>

      {{#each this.filteredNamespaces as |ns|}}
        <D.Checkmark
          data-test-vault-reporting-namespace-menu-item={{ns}}
          @selected={{eq ns this.selectedNamespace}}
          {{on "click" (fn this.handleNamespaceSelection ns D.close)}}
        >
          {{ns}}
        </D.Checkmark>
      {{/each}}
    </HdsDropdown>
  </template>
}
